import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Linking, View } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { format, isAfter, isBefore, isEqual, parse } from 'date-fns'
import { cloneDeep, isEmpty, isNil, remove } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getGuardianInfoForEpisode,
  isNoteSubmittedForAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { careConsents, clientIntakesLCx } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/constants'
import { ActivityGroupTypes, AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import {
  getEffectiveRecipientLyraId,
  getIsAssignmentGoogleActionPlan,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import {
  getPossessivePronoun,
  getReflexivePronoun,
  isAgeTeen,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import {
  ConsentBannerRecipients,
  getConsentBannerMessage,
} from '@lyrahealth-inc/shared-app-logic/src/features/consent/messages'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  isDAEngageProgram,
  isDAMinorProgram,
  isTeenProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { getAgeISO } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  ActivityGroupType,
  Assignment,
  AssignmentGroup,
} from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import {
  Curriculum,
  Episode,
  EpisodeContents,
  EpisodeParticipantRole,
  EpisodeStates,
  RecipientType,
} from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import NotificationBanner from '@lyrahealth-inc/ui-core/lib/atoms/notificationBanner/NotificationBanner'
import { AIStatusBadge, DigestType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiStatusBadge/AIStatusBadge'
import { ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import {
  DropdownButton,
  DropdownMenuItem,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import { BellIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BellIcon'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BadgeShape } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { ClientIntakeCancellationPolicyText } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientIntakeCancellationPolicyText/ClientIntakeCancellationPolicyText'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import {
  AssignmentsList,
  AssignmentsListProps,
  ItemAction,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/assignmentsList/AssignmentsList'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { AssignmentReviewPage } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/assignmentReviewPage/AssignmentReviewPage'
import { InfoSheet } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/infoSheet/InfoSheet'
import { Lesson } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/lesson/Lesson.web'
import { SSCAssignmentsList } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/sscAssignmentsList/SSCAssignmentsList'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import AssignmentAISummary from './AssignmentAISummary'
import styles from './assignments.module.scss'
import { canManageAssignmentsForEpisode } from './data/assignmentAccess'
import {
  useBulkUnsetAssignmentMutation,
  useGetAssignmentSummaryQuery,
  useUnsetAssignmentMutation,
  useUpdateAssignmentResponseMutation,
} from './data/assignmentsApi'
import { clearSelectedAssignmentId, setSelectedAssignmentId } from './data/assignmentsSlice'
import { actions as mixpanelActions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { programConfig as programDefinitions } from '../../common/constants/appConstants'
import {
  CLIENT_NOTES,
  CLIENTS_ASSIGNMENT_DETAILS,
  CLIENTS_CONTENT_LIBRARY,
  CLIENTS_TRACK_LIBRARY,
} from '../../common/constants/routingConstants'
import { getEpisodeDropdownItemText, isLCxProvider, isProviderUS } from '../../common/utils/utils'
import { getAuthConfig, getAuthSupervisor, getAuthUser } from '../../data/auth/authSelectors'
import { getClientCurrentSessionCount } from '../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../data/lyraTherapy/contentSelectors'
import { useAppDispatch } from '../../data/store'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { useVideoSessionBannerHeight } from '../../hooks/useVideoSessionBannerHeight'
import { useGetNotesQuery } from '../clientNotes/data/notesApi'
import ClientAssignmentsPDF from '../clients/clientDetails/clientRecords/ClientAssignmentsPDF'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { updateGetClientsCountsCache } from '../clients/data/ltClientsApi'
import { getFutureSessions } from '../configAssignmentContainer/assignmentSessionUtils'
import { getConsentBannerStatusForAssignment } from '../consent/consentUtils'
import { useGetEpisodeConsentsQuery } from '../consent/data/consentApi'
import { getLTVideoSessionOpen } from '../data/ltVideoSelectors'
import { useUpdateCurriculumMutation } from '../episodes/data/episodesApi'
import { useAssignmentsData } from './data/hooks/useAssignmentsData'
import { useSelectedAssignment } from './data/hooks/useSelectedAssignment'
import { useEpisodesForSelectedClient } from '../episodes/data/hooks/useEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

type AssignmentsProps = {
  setClientAssignmentsPDFContent?: (content: any) => void
  pdfContentStartDate?: string
  pdfContentEndDate?: string
}

const BULK_UNASSIGN_PROGRAMS_DISALLOWED = [ProgramNames.SingleSessionCoaching, ProgramNames.Coaching]

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: spacing['32px'],
}))

const ButtonsContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'flex-end',
})

const PrimaryButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['16px'],
}))

const AssignmentHeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 1, // For Session selector dropdown
  flexWrap: 'wrap',
  gap: spacing['12px'],
}))

const AssignmentBulkActionContainer = styled.View({
  flexDirection: 'row',
})

const AssignmentBulkActionCancelButton = styled(TextButton)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignSelf: 'center',
  marginLeft: spacing['16px'],
}))

const RecipientSectionHeader = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundSection,
  paddingVertical: spacing['8px'],
  paddingHorizontal: spacing['16px'],
  borderRadius: 8,
  marginBottom: spacing['8px'],
}))

const RecipientSectionContainer = styled.View<{ theme: ThemeType; isFirst?: boolean }>(
  ({ theme: { spacing }, isFirst }: { theme: ThemeType; isFirst?: boolean }) => ({
    marginBottom: spacing['24px'],
    ...(isFirst && { marginTop: spacing['16px'] }),
  }),
)

const RecipientSectionActionContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  marginBottom: spacing['8px'],
}))

type RecipientSectionProps = {
  recipientType: RecipientType
  name: string
  subtitle: string
  activities: Assignment[]
  futureEpisodes: (Assignment | EpisodeContents)[]
  addActivityHandler?: () => void
  isFirst?: boolean
  canShowBulkEditControls: boolean
  bulkEditMode: boolean
  bulkEditRecipientType: RecipientType | null
  bulkActionSelectedItems: (Assignment | EpisodeContents)[]
  onConfirmPress: () => void
  onBulkEditModePress: (recipientType?: RecipientType) => void
  sharedAssignmentsListProps: Omit<
    AssignmentsListProps,
    'assignments' | 'addActivityHandler' | 'futureEpisodes' | 'bulkEditMode'
  >
}

const RecipientSection: React.FC<RecipientSectionProps> = ({
  recipientType,
  name,
  subtitle,
  activities,
  futureEpisodes,
  addActivityHandler,
  isFirst,
  canShowBulkEditControls,
  bulkEditMode,
  bulkEditRecipientType,
  bulkActionSelectedItems,
  onConfirmPress,
  onBulkEditModePress,
  sharedAssignmentsListProps,
}) => (
  <RecipientSectionContainer isFirst={isFirst} testID={tID(`Assignments-${recipientType}Section`)}>
    <RecipientSectionHeader testID={tID(`Assignments-${recipientType}SectionHeader`)}>
      <Title text={name} size={TitleSize.XSMALL} />
      <BodyText text={subtitle} size={BodyTextSize.SMALL} />
    </RecipientSectionHeader>
    {canShowBulkEditControls && (
      <RecipientSectionActionContainer>
        {bulkEditMode && bulkEditRecipientType === recipientType ? (
          <AssignmentBulkActionContainer>
            <PrimaryButton
              disabled={bulkActionSelectedItems.length === 0}
              onPress={onConfirmPress}
              text='Remove'
              testID={tID(`Assignments-${recipientType}-bulkConfirm`)}
            />
            <AssignmentBulkActionCancelButton
              onPress={() => onBulkEditModePress(recipientType)}
              text='Cancel'
              testID={tID(`Assignments-${recipientType}-bulkEdit-cancel`)}
            />
          </AssignmentBulkActionContainer>
        ) : (
          <PrimaryButton
            onPress={() => onBulkEditModePress(recipientType)}
            text='Select'
            testID={tID(`Assignments-${recipientType}-bulkEdit`)}
          />
        )}
      </RecipientSectionActionContainer>
    )}
    <AssignmentsList
      {...sharedAssignmentsListProps}
      addActivityHandler={addActivityHandler}
      assignments={activities}
      futureEpisodes={futureEpisodes}
      bulkEditMode={bulkEditMode && bulkEditRecipientType === recipientType}
    />
  </RecipientSectionContainer>
)

const DBTTeensSessionDropdown = styled(DropdownButton)<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  backgroundColor: 'transparent',
  borderRadius: 8,
  borderColor: colors.textInactive,
}))

const ModalContainer = styled(Modal)<{ videoSessionOpen: boolean }>(
  ({ theme, videoSessionOpen }: { theme: ThemeType; videoSessionOpen: boolean }) => ({
    marginTop: videoSessionOpen ? '120px' : theme.spacing['32px'],
    width: '600px',
  }),
)

const Assignments: React.FC<AssignmentsProps> = ({
  setClientAssignmentsPDFContent,
  pdfContentStartDate,
  pdfContentEndDate,
}: AssignmentsProps) => {
  const intl = useIntl()
  const { colors: themeColors } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { activityActions, notifications = [] } = useOutletContext<{
    activityActions?: any[]
    notifications?: any[]
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const isDAEngageProvider = useIsEngageDAProvider()
  const [updateCurriculum] = useUpdateCurriculumMutation()
  const [unsetAssignment] = useUnsetAssignmentMutation()
  const [bulkUnsetAssignment] = useBulkUnsetAssignmentMutation()
  const [updateAssignmentResponse] = useUpdateAssignmentResponseMutation()

  const currentSessionCount = useSelector(getClientCurrentSessionCount) ?? 0
  const contents = useSelector(getLyraTherapyContentsData)
  const user = useSelector(getAuthUser)
  const supervisor = useSelector(getAuthSupervisor)
  const { data: episodes } = useEpisodesForSelectedClient()
  const selectedAssignment = useSelectedAssignment()
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const config = useSelector(getAuthConfig)
  const dispatch = useAppDispatch()

  const [session, setSession] = useState(location?.state?.sessionCount || currentSessionCount)
  const [showModal, setShowModal] = useState(false)
  const prevCurrentSessionCount = useRef<number>(currentSessionCount)
  const [modalContent, setModalContent] = useState<React.ReactElement | null>(null)
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [bulkEditRecipientType, setBulkEditRecipientType] = useState<RecipientType | null>(null)
  const [bulkActionSelectedItems, setBulkActionSelectedItems] = useState<(Assignment | EpisodeContents)[]>([])
  const [unassignableAssignmentsList, setUnassignableAssignmentsList] = useState<(Assignment | EpisodeContents)[]>([])
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)

  const clearBulkEditState = useCallback(() => {
    setBulkEditMode(false)
    setBulkEditRecipientType(null)
    setBulkActionSelectedItems([])
    setUnassignableAssignmentsList([])
  }, [])

  const {
    enableAIClientIntakeSummaryFetch,
    showAIClientIntakeSummary,
    isPreferredNameEnabled,
    clientListWithEnabledPreferredName,
    isDAProviderMigrated,
    isDAMinorProgramEnabled,
    isConsentTrackingEnabled,
  } = useFlags()

  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { data: allAppointments } = useClientAppointmentsData()
  const { currentData: episodeConsentsData } = useGetEpisodeConsentsQuery(
    isConsentTrackingEnabled && selectedEpisode?.id ? { episodeId: selectedEpisode.id } : skipToken,
  )
  const programConfig = selectedEpisode ? programDefinitions[selectedEpisode.program_name] : undefined
  const curriculum = selectedEpisode ? selectedEpisode.curriculum : undefined

  const providerEpisodesInfoPDF = episodes
    ?.filter((episode: Episode) => episode.provider_id === user?.id)
    .map((episode: Episode) => {
      return {
        text: getEpisodeDropdownItemText(intl, episode),
        id: episode.id,
      }
    })
  const { data: allAssignments } = useAssignmentsData()

  const allProviderAssignmentsPDF = allAssignments?.filter((assignment) =>
    providerEpisodesInfoPDF?.map((episodeInfo) => episodeInfo.id).includes(assignment.episode_id),
  )
  const assignments = useMemo(
    () => allAssignments?.filter((assignment) => assignment.episode_id === selectedEpisode?.id) ?? [],
    [allAssignments, selectedEpisode?.id],
  )
  const activities = assignments.filter((assignment) => assignment.content.group !== 'assessment')
  const assessments = assignments.filter((assignment) => assignment.content.group === 'assessment')
  const currentActivities = activities.filter(
    (assignment) =>
      assignment.session_period === currentSessionCount ||
      // filter so that session period 0s/auto assigned activities stay visible in the first session
      (currentSessionCount === 1 && assignment.session_period === 0) ||
      // Action Plan Exercise should always be visible in the current activities
      getIsAssignmentGoogleActionPlan(assignment),
  )

  useEffect(() => {
    setIsPDFContentInitialized(false)
  }, [pdfContentStartDate, pdfContentEndDate])

  useEffect(() => {
    if (
      isPDFContentInitialized ||
      isNil(allProviderAssignmentsPDF) ||
      !setClientAssignmentsPDFContent ||
      !allAppointments
    ) {
      return
    }
    setClientAssignmentsPDFContent(
      ClientAssignmentsPDF({
        appointments: allAppointments,
        assignments: allProviderAssignmentsPDF,
        pdfContentStartDate,
        pdfContentEndDate,
        providerEpisodesInfo: providerEpisodesInfoPDF ?? [],
      }),
    )
    setIsPDFContentInitialized(true)
  }, [
    allProviderAssignmentsPDF,
    isPDFContentInitialized,
    setClientAssignmentsPDFContent,
    pdfContentStartDate,
    pdfContentEndDate,
    providerEpisodesInfoPDF,
    selectedEpisode?.id,
    allAppointments,
  ])

  useEffect(() => {
    const assignmentId = searchParams.get('assignmentId')
    if (!assignmentId) {
      return
    }

    const assignment = assignments.find((assignment) => assignment.id === assignmentId)
    if (assignment) {
      dispatch(setSelectedAssignmentId(assignmentId))
      setShowModal(true)
      setSearchParams((prev) => {
        prev.delete('assignmentId')
        return prev
      })
    }
  }, [assignments, dispatch, searchParams, setSearchParams])
  const { data: clientDetails } = useSelectedClientData()

  const appointments = useMemo(
    () => allAppointments?.filter((appt: Appointment) => appt.episodeId === selectedEpisode?.id) ?? [],
    [allAppointments, selectedEpisode?.id],
  )
  const { data: allNotes, isSuccess: fetchedNotes } = useGetNotesQuery(
    selectedEpisode?.id
      ? {
          episodeId: selectedEpisode.id,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  const notesNotifications =
    !isDAEngageProvider && fetchedNotes
      ? appointments
          .filter(
            (appointment) =>
              isBefore(getDateFromAppointment(appointment), new Date()) &&
              !['canceled', 'missed'].includes(appointment.appointmentStatus) &&
              (selectedEpisode?.program_name !== ProgramNames.DialecticalBehaviorTherapyTeens ||
                appointment.lyraProviderId === user?.id),
          )
          .filter(
            (appointment) =>
              !allNotes?.some(
                (note) =>
                  note.appointment_id === appointment.appointmentId &&
                  note.status === 'completed' &&
                  note.episode_id === appointment.episodeId,
              ),
          )
          .map((appointment) => ({
            text: `Add Session ${appointment.sessionNumber} Notes`,
            icon: BellIcon,
            clickAction: () => navigate(CLIENT_NOTES.route),
          }))
      : []
  const activityNotifications = [...notifications, ...notesNotifications]

  const isLCxProviderCheck = isLCxProvider(user)
  const isProviderUSCheck = isProviderUS(user)

  const intakeApppointmentId = useMemo(() => {
    const intakeSession =
      Array.isArray(appointments) && appointments?.find((appointment) => appointment.sessionNumber === 1)
    return intakeSession ? intakeSession.appointmentId : undefined
  }, [appointments])

  const isIntakeSessionNoteSubmitted = useMemo(
    () => isNoteSubmittedForAppointment(allNotes, intakeApppointmentId),
    [allNotes, intakeApppointmentId],
  )

  const updateResponseThenFetchAssignments = useCallback(
    (response: any) => {
      updateAssignmentResponse(response)
        .unwrap()
        .then(() => {
          updateGetClientsCountsCache(dispatch, user?.id, clientDetails)
        })
    },
    [clientDetails, dispatch, updateAssignmentResponse, user?.id],
  )

  const markedAsViewedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!showModal || isEmpty(selectedAssignment) || !isEmpty(supervisor)) {
      return
    }
    const response = selectedAssignment?.assignment_responses?.[0]
    if (response && !response.viewed && !markedAsViewedRef.current.has(response.id)) {
      markedAsViewedRef.current.add(response.id)
      updateResponseThenFetchAssignments({ ...response, viewed: true })
    }
  }, [showModal, selectedAssignment, supervisor, updateResponseThenFetchAssignments])

  const onBulkUnassign = useCallback(() => {
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.CONFIRM_BULK_UNASSIGN,
    })

    const selectedCurriculum = curriculum?.findIndex((sessionObject: Curriculum) => sessionObject.session === session)
    const newCurriculum = cloneDeep(curriculum)
    const assignmentIdsToUnset: string[] = []
    bulkActionSelectedItems.forEach((item) => {
      // Build list of Assignments to unset
      if (item.id) {
        assignmentIdsToUnset.push(item.id)
      }
      // Update Curriculum if in future session
      if (newCurriculum && typeof selectedCurriculum === 'number' && session > currentSessionCount) {
        if (item.id) {
          newCurriculum[selectedCurriculum].contents = newCurriculum[selectedCurriculum].contents.filter(
            (content: EpisodeContents) => content.id != item.id,
          )
        } else {
          remove(newCurriculum[selectedCurriculum].contents, item as EpisodeContents)
        }
      }
    })

    if (session > currentSessionCount) {
      updateCurriculum({ id: selectedEpisode!.id, curriculum: newCurriculum! })
      assignmentIdsToUnset.forEach((id) => unsetAssignment({ id }))
    } else if (assignmentIdsToUnset.length > 0) {
      bulkUnsetAssignment(assignmentIdsToUnset)
    }

    setModalContent(null)
    clearBulkEditState()
  }, [
    bulkActionSelectedItems,
    bulkUnsetAssignment,
    clearBulkEditState,
    currentSessionCount,
    curriculum,
    selectedEpisode,
    session,
    unsetAssignment,
    updateCurriculum,
  ])

  const onCheckboxPress = useCallback(
    (assignment: Assignment | EpisodeContents) => {
      if (bulkActionSelectedItems.includes(assignment)) {
        setBulkActionSelectedItems(bulkActionSelectedItems.filter((id) => id !== assignment))
      } else {
        setBulkActionSelectedItems([...bulkActionSelectedItems, assignment])
      }
    },
    [bulkActionSelectedItems],
  )

  const onConfirmPress = useCallback(() => {
    setModalContent(
      <>
        <HeaderContainer>
          <Title
            size={TitleSize.MEDIUM}
            text={'Are you sure you want to remove all selected activities from this session?'}
          />
        </HeaderContainer>
        <ButtonsContainer>
          <TertiaryButton
            onPress={() => setModalContent(null)}
            text={<FormattedMessage defaultMessage='Cancel' description='Cancel button to go back to session page' />}
          ></TertiaryButton>
          <PrimaryButtonContainer>
            <PrimaryButton
              onPress={onBulkUnassign}
              text={'Confirm'}
              testID={tID('Assignments-modal-bulkConfirm')}
            ></PrimaryButton>
          </PrimaryButtonContainer>
        </ButtonsContainer>
      </>,
    )
  }, [onBulkUnassign])

  const onSelectAllPress = useCallback(() => {
    setBulkActionSelectedItems(bulkActionSelectedItems.length === 0 ? unassignableAssignmentsList : [])
  }, [bulkActionSelectedItems.length, unassignableAssignmentsList])

  const appointmentDateFromSessionCount = useCallback(
    (count: number) => {
      const selectedAppointment =
        Array.isArray(appointments) &&
        appointments?.find((appointment: Appointment) => appointment.sessionNumber === count)
      if (selectedAppointment) {
        const parsedDate = parse(selectedAppointment.startDate, 'yyyy-MM-dd', new Date())
        if (!Number.isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'MMM do')
        }
      }
      return 'unscheduled'
    },
    [appointments],
  )

  const getAssessmentsForSession = useCallback(
    (sessionCount: number) => {
      if (!sessionCount) {
        return []
      }
      let previousAppt: Appointment | undefined, sessionStartDate: Date | undefined, sessionEndDate: Date | undefined
      const sessionAppt =
        Array.isArray(appointments) &&
        appointments?.find((appt: Appointment) => {
          return appt.appointmentStatus === 'completed' && appt.sessionNumber === sessionCount
        })
      if (sessionAppt) {
        sessionEndDate = getDateFromAppointment(sessionAppt)
      }
      if (sessionCount > 1) {
        previousAppt = Array.isArray(appointments)
          ? appointments?.find(
              (appt: Appointment) => appt.appointmentStatus === 'completed' && appt.sessionNumber === sessionCount - 1,
            )
          : undefined
        if (previousAppt) {
          sessionStartDate = getDateFromAppointment(previousAppt)
        }
      } else if (selectedEpisode?.start_date) {
        sessionStartDate = new Date(selectedEpisode.start_date)
      }

      if (!sessionStartDate) {
        console.warn(`the start date for session ${sessionCount} hasn't been found`)
        return []
      }
      const sessionAssessments = assessments?.filter((assessment: Assignment) => {
        const updateDate = new Date(assessment.update_date)
        const submitDateString = assessment.assignment_responses?.[0]?.submit_date
        const submitDate = submitDateString ? new Date(submitDateString) : undefined
        switch (assessment.status) {
          case AssignmentStatuses.missed:
            return (
              !Number.isNaN(updateDate.getTime()) &&
              (!sessionStartDate || isEqual(updateDate, sessionStartDate) || isAfter(updateDate, sessionStartDate)) &&
              (!sessionEndDate || isBefore(updateDate, sessionEndDate))
            )
          case AssignmentStatuses.completed:
            return (
              !!submitDate &&
              !Number.isNaN(submitDate.getTime()) &&
              (!sessionStartDate || isEqual(submitDate, sessionStartDate) || isAfter(submitDate, sessionStartDate)) &&
              (!sessionEndDate || isBefore(submitDate, sessionEndDate))
            )
          default:
            // Assessment is active (status is new or in-progress)
            return currentSessionCount === sessionCount
        }
      })
      return sessionAssessments
    },
    [appointments, selectedEpisode?.start_date, assessments, currentSessionCount],
  )

  const programName = selectedEpisode?.program_name
  const clientFirstName = clientDetails?.first_name
  const isDBTTeens = programName === ProgramNames.DialecticalBehaviorTherapyTeens
  const canManageEpisodeAssignments = canManageAssignmentsForEpisode(selectedEpisode, user?.id)

  const guardianInfo = useMemo(
    () => getGuardianInfoForEpisode(appointments, selectedEpisode?.participants),
    [appointments, selectedEpisode?.participants],
  )

  const clientFullName = clientDetails ? `${clientDetails.first_name} ${clientDetails.last_name}` : ''

  const { guardianFirstName, guardianLastName } = guardianInfo
  const caregiverFullName = [guardianFirstName, guardianLastName].filter(Boolean).join(' ')
  const teenRecipientLyraId = selectedEpisode?.patient_id
  const caregiverRecipientLyraId = guardianInfo.guardianLyraId

  /** Resolves a consent recipient to the client or their caregiver, which decides the banner copy */
  const getConsentRecipientInfo = useCallback(
    (recipientLyraId: string | undefined): ConsentBannerRecipients | undefined => {
      if (!recipientLyraId || !selectedEpisode || !clientFirstName) return undefined

      if (recipientLyraId === selectedEpisode.patient_id) {
        return { client: { firstName: clientFirstName, pronouns: clientDetails?.pronouns } }
      }

      const isCaregiverParticipant = selectedEpisode.participants?.some(
        (participant) =>
          participant.participant_lyra_id === recipientLyraId &&
          (participant.role === EpisodeParticipantRole.PRIMARY_GUARDIAN ||
            participant.role === EpisodeParticipantRole.SECONDARY_PATIENT),
      )
      const isAppointmentGuardian = appointments.some(
        (appointment: Appointment) =>
          appointment.episodeId === selectedEpisode.id && appointment.guardianLyraId === recipientLyraId,
      )
      if (!isCaregiverParticipant && !isAppointmentGuardian) return undefined

      const matchingGuardianAppointment = appointments.find(
        (appointment: Appointment) =>
          appointment.episodeId === selectedEpisode.id && appointment.guardianLyraId === recipientLyraId,
      )
      // The message falls back to "the caregiver" when the name is unknown
      return { caregiver: { firstName: matchingGuardianAppointment?.guardianFirstName } }
    },
    [appointments, clientDetails?.pronouns, clientFirstName, selectedEpisode],
  )

  const getRecipientTypeForItem = useCallback(
    (item?: Assignment | EpisodeContents): RecipientType => {
      const assignment = item as Assignment | undefined
      const recipientLyraId = assignment?.recipient_id

      return getEffectiveRecipientLyraId(recipientLyraId, teenRecipientLyraId) === teenRecipientLyraId
        ? RecipientType.TEEN
        : RecipientType.CAREGIVER
    },
    [teenRecipientLyraId],
  )

  const getRecipientLyraId = useCallback(
    (recipientType: RecipientType) => {
      return recipientType === RecipientType.CAREGIVER ? caregiverRecipientLyraId : teenRecipientLyraId
    },
    [caregiverRecipientLyraId, teenRecipientLyraId],
  )

  const currentSessionAssignments = useMemo(() => {
    return getAssessmentsForSession(session)
  }, [getAssessmentsForSession, session])

  const isAssignmentAnIntake = useCallback(
    (assignmentName?: string) => {
      if (isNil(assignmentName)) {
        return false
      }
      return (
        [programConfig?.clientIntake, programConfig?.oldClientIntake, programConfig?.clientIntakeNonUS].includes(
          assignmentName,
        ) ?? false
      )
    },
    [programConfig?.clientIntake, programConfig?.clientIntakeNonUS, programConfig?.oldClientIntake],
  )
  const clientIntakeAssignment = useMemo(() => {
    return programName && Array.isArray(currentSessionAssignments)
      ? currentSessionAssignments?.find((item) => {
          return isAssignmentAnIntake(item?.content?.name)
        })
      : undefined
  }, [currentSessionAssignments, isAssignmentAnIntake, programName])

  const shouldFetchSummaryStatus =
    !isNil(allNotes) &&
    !isNil(programName) &&
    clientIntakeAssignment &&
    isAssignmentAnIntake(clientIntakeAssignment?.content?.name) &&
    currentSessionCount === 1 &&
    clientIntakeAssignment?.status === AssignmentStatuses.completed &&
    enableAIClientIntakeSummaryFetch &&
    !isIntakeSessionNoteSubmitted &&
    isProviderUSCheck &&
    isLCxProviderCheck

  const { data: clientIntakeSummaryResponse } = useGetAssignmentSummaryQuery(
    shouldFetchSummaryStatus && clientIntakeAssignment?.id && user?.id
      ? {
          assignmentId: clientIntakeAssignment.id,
          providerId: user.id,
          statusOnly: true,
        }
      : skipToken,
  )

  // return all selected session assignments, and add the client intake AI status badge if available
  const getCurrentAssignments = useCallback(
    (assignments) => {
      return assignments?.map((currentSessionAssignment: Assignment) => {
        const copyOfCurrentAssignment = { ...currentSessionAssignment }
        if (
          !isNil(programName) &&
          isAssignmentAnIntake(copyOfCurrentAssignment?.content?.name) &&
          enableAIClientIntakeSummaryFetch
        ) {
          const isThisTheIntakeAssignment = currentSessionAssignment.id === clientIntakeAssignment?.id
          const shouldShowBadge =
            isThisTheIntakeAssignment &&
            currentSessionAssignment.status === AssignmentStatuses.completed &&
            !isIntakeSessionNoteSubmitted &&
            showAIClientIntakeSummary &&
            clientIntakeSummaryResponse?.status

          if (shouldShowBadge) {
            copyOfCurrentAssignment.badge = (
              <AIStatusBadge
                digestType={DigestType.SUMMARY}
                status={clientIntakeSummaryResponse.status}
                shape={BadgeShape.RECT}
              />
            )
          }
        }
        return copyOfCurrentAssignment
      })
    },
    [
      enableAIClientIntakeSummaryFetch,
      isAssignmentAnIntake,
      isIntakeSessionNoteSubmitted,
      programName,
      showAIClientIntakeSummary,
      clientIntakeAssignment?.id,
      clientIntakeSummaryResponse?.status,
    ],
  )

  const changeSessionPeriod = useCallback(
    (session: number, period: string) => {
      track({ event: 'FILTER_CHANGE', action: 'CHANGE_SESSION_PERIOD', details: period })
      setSession(session)
      clearBulkEditState()
    },
    [clearBulkEditState],
  )

  const getDropdownItems = useCallback(() => {
    const dropdownItems: DropdownMenuItem[] = []
    const chunkedPastAssignments: Record<number, Assignment[]> = {}

    const renderSubtext = (numActivities: number, sessionCount: number) => {
      const pluralize = numActivities === 1 ? 'y' : 'ies'
      const date = appointmentDateFromSessionCount(sessionCount)
      const dateText = date === 'unscheduled' ? sessionCount : 'on ' + date
      return numActivities + ` activit${pluralize} for session ` + dateText
    }

    // Past Sessions
    if (currentSessionCount > 1) {
      dropdownItems.push({ text: 'Past', id: 'past-header', header: true, selectHandler: () => {} })
      for (let i = 1; i < currentSessionCount; i++) {
        const filteredActivities = activities.filter(
          (assignment: Assignment) =>
            assignment.session_period === i ||
            // filter so that session period 0s/auto assigned activities stay visible in the first session
            (i === 1 && assignment.session_period === 0 && !getIsAssignmentGoogleActionPlan(assignment)),
        )
        const assessments = getAssessmentsForSession(i)
        chunkedPastAssignments[i] = [...filteredActivities, ...assessments]
      }

      Object.entries(chunkedPastAssignments).forEach(([sessionCount, assignments]) => {
        const convertedSessionCount = parseInt(sessionCount)
        dropdownItems.push({
          text: 'Session ' + convertedSessionCount,
          subText: renderSubtext((assignments as Assignment[]).length, convertedSessionCount),
          id: convertedSessionCount.toString(),
          selectHandler: () => {
            changeSessionPeriod(convertedSessionCount, 'Past')
          },
        })
      })
    }

    // Current Sessions
    const assignments = [...currentActivities, ...getAssessmentsForSession(currentSessionCount)]
    dropdownItems.push(
      { text: 'Current', id: 'current-header', header: true, selectHandler: () => {} },
      {
        text: 'Session ' + currentSessionCount,
        subText: renderSubtext(assignments.length, currentSessionCount),
        id: currentSessionCount.toString(),
        selectHandler: () => {
          changeSessionPeriod(currentSessionCount, 'Current')
        },
      },
    )

    // Future Sessions
    if (curriculum) {
      dropdownItems.push({ text: 'Future', id: 'future-header', header: true, selectHandler: () => {} })
      getFutureSessions(curriculum, currentSessionCount, programConfig?.canPlanFutureAssignments).forEach((session) => {
        const assignments =
          curriculum.find((sessionObject: Curriculum) => sessionObject.session === session)?.contents ?? []
        dropdownItems.push({
          text: 'Session ' + session,
          subText: renderSubtext(assignments.length, session),
          id: session.toString(),
          selectHandler: () => {
            changeSessionPeriod(session, 'Future')
          },
        })
      })
    }
    return dropdownItems
  }, [
    curriculum,
    programConfig,
    currentSessionCount,
    currentActivities,
    getAssessmentsForSession,
    appointmentDateFromSessionCount,
    activities,
    changeSessionPeriod,
  ])

  const onModalClose = () => {
    setShowModal(false)
    dispatch(clearSelectedAssignmentId())
  }

  useEffect(() => {
    if (prevCurrentSessionCount.current != currentSessionCount) {
      setSession(currentSessionCount)
    }
    prevCurrentSessionCount.current = currentSessionCount
  }, [currentSessionCount])

  const addTrack = () => {
    track({ event: 'BUTTON_PRESS', action: 'ADD_ACTIVITY' })
    navigate(CLIENTS_TRACK_LIBRARY.route, { state: { sessionCount: session } })
  }

  const addActivity = (recipientType?: RecipientType) => {
    track({ event: 'BUTTON_PRESS', action: 'ADD_ACTIVITY' })
    const recipientLyraId = recipientType ? getRecipientLyraId(recipientType) : undefined
    navigate(CLIENTS_CONTENT_LIBRARY.route, {
      state: {
        sessionCount: session,
        ...(recipientLyraId && { recipientLyraId }),
        ...(recipientType && { recipientType }),
      },
    })
  }

  const getActivitiesToDisplay = useCallback((): Assignment[] => {
    /* session: Indicates the current selected session in the provider portal filter
     * currentSessionCount: Indicates the current session number for the client
     */

    if (isDAEngageProvider) {
      return assignments
    }

    // Past Session
    if (session < currentSessionCount) {
      const filteredActivities = activities.filter(
        (assignment: Assignment) =>
          assignment.session_period === session ||
          // filter so that session period 0s/auto assigned activities stay visible in the first session
          (assignment.session_period === 0 && session === 1 && !getIsAssignmentGoogleActionPlan(assignment)),
      )
      const assessments = getAssessmentsForSession(session)
      return [...filteredActivities, ...assessments]

      // Current Session
    } else if (session === currentSessionCount) {
      return [...currentActivities, ...(getCurrentAssignments(currentSessionAssignments) || [])]
    } else {
      return []
    }
  }, [
    activities,
    assignments,
    currentActivities,
    currentSessionAssignments,
    currentSessionCount,
    getAssessmentsForSession,
    getCurrentAssignments,
    isDAEngageProvider,
    session,
  ])

  const activitiesToDisplay = useMemo(() => getActivitiesToDisplay(), [getActivitiesToDisplay])

  const consentAssignmentsInfo = useMemo(() => {
    const consents = episodeConsentsData?.consents
    if (!isConsentTrackingEnabled || !consents?.length || !clientFirstName) {
      return undefined
    }

    const info: Record<string, { inlineInfoBannerMessage: string }> = {}
    assignments.forEach((activity) => {
      if (
        !('content' in activity) ||
        !careConsents.includes(activity.content.name) ||
        activity.status === AssignmentStatuses.completed ||
        activity.status === AssignmentStatuses.missed
      ) {
        return
      }
      const recipientLyraId = getEffectiveRecipientLyraId(
        activity.recipient_id,
        activity.patient_id || selectedEpisode?.patient_id,
      )
      const bannerStatus = getConsentBannerStatusForAssignment(consents, activity.id, recipientLyraId)
      if (!bannerStatus) return

      const recipient = getConsentRecipientInfo(recipientLyraId)
      if (!recipient) return

      const message = getConsentBannerMessage(intl, bannerStatus, recipient)
      if (message) info[activity.id] = { inlineInfoBannerMessage: message }
    })

    return Object.keys(info).length ? info : undefined
  }, [
    assignments,
    clientFirstName,
    episodeConsentsData?.consents,
    getConsentRecipientInfo,
    intl,
    isConsentTrackingEnabled,
    selectedEpisode?.patient_id,
  ])

  const getClientIntakeCancellationPolicyText = (assignment: Assignment) => {
    if (assignment?.assignment_responses?.[0]?.response?.cancellationConsentText) {
      return (
        <ClientIntakeCancellationPolicyText
          policyText={assignment?.assignment_responses[0]?.response?.cancellationConsentText}
          navigateToCancellationPolicyFAQ={() => {}}
        />
      )
    } else {
      return null
    }
  }

  const getFutureEpisodesToDisplay = useCallback((): (Assignment | EpisodeContents)[] => {
    // Future Session
    let futureEpisodeActivities: (Assignment | EpisodeContents)[] = []

    if (session > currentSessionCount) {
      const sessionObject =
        Array.isArray(curriculum) && curriculum?.find((sessionObject: Curriculum) => sessionObject.session === session)

      if (sessionObject) {
        futureEpisodeActivities = sessionObject.contents.map((content: EpisodeContents) => {
          if (content.id) {
            const activity = Array.isArray(activities) && activities?.find((activity) => activity.id === content.id)
            return activity || content
          } else {
            return content
          }
        })
      }
    }
    return futureEpisodeActivities
  }, [activities, currentSessionCount, curriculum, session])

  const futureEpisodesToDisplay = useMemo(() => getFutureEpisodesToDisplay(), [getFutureEpisodesToDisplay])

  const { teenActivities, caregiverActivities, teenFutureEpisodes, caregiverFutureEpisodes } = useMemo(() => {
    if (!isDBTTeens) {
      return {
        teenActivities: [],
        caregiverActivities: [],
        teenFutureEpisodes: [],
        caregiverFutureEpisodes: [],
      }
    }

    return {
      teenActivities: activitiesToDisplay.filter(
        (activity) => getRecipientTypeForItem(activity) === RecipientType.TEEN,
      ),
      caregiverActivities: activitiesToDisplay.filter(
        (activity) => getRecipientTypeForItem(activity) === RecipientType.CAREGIVER,
      ),
      teenFutureEpisodes: futureEpisodesToDisplay.filter(
        (activity) => getRecipientTypeForItem(activity) === RecipientType.TEEN,
      ),
      caregiverFutureEpisodes: futureEpisodesToDisplay.filter(
        (activity) => getRecipientTypeForItem(activity) === RecipientType.CAREGIVER,
      ),
    }
  }, [activitiesToDisplay, futureEpisodesToDisplay, getRecipientTypeForItem, isDBTTeens])

  const assignmentClicked = useCallback(
    (assignment: Assignment) => {
      const contentGroup: ActivityGroupType = assignment?.content?.group
      switch (contentGroup) {
        case ActivityGroupTypes.assessment:
          if (assignment.response_count === 0) return
          dispatch(setSelectedAssignmentId(assignment.id))
          const isClientIntake = isAssignmentAnIntake(assignment?.content?.name)
          const aiSummaryStatus = isClientIntake ? clientIntakeSummaryResponse?.status : undefined
          const assignmentResponseId =
            assignment?.assignment_responses && assignment?.assignment_responses?.length
              ? assignment?.assignment_responses[0].id
              : ''
          track({
            event: isClientIntake ? mixpanelEvents.CLIENT_INTAKE_FORM_OPEN_CLIENT_HOME : 'REVIEW_CONTENT',
            details: {
              type: ActivityGroupTypes.assessment,
              title: assignment?.content?.title,
              summaryStatus: isClientIntake ? aiSummaryStatus : undefined,
              userId: user?.id,
              assignmentId: assignment?.id,
              assignmentResponseId: assignmentResponseId,
            },
          })
          setShowModal(true)
          break
        case ActivityGroupTypes.lesson:
          dispatch(setSelectedAssignmentId(assignment.id))
          track({
            event: 'REVIEW_CONTENT',
            details: {
              type: ActivityGroupTypes.lesson,
              title: assignment?.content?.title,
            },
          })
          setShowModal(true)
          break
        case ActivityGroupTypes.infosheet:
          dispatch(setSelectedAssignmentId(assignment.id))
          track({
            event: 'REVIEW_CONTENT',
            details: {
              type: ActivityGroupTypes.infosheet,
              title: assignment?.content?.title,
            },
          })
          setShowModal(true)
          break
        default:
          dispatch(setSelectedAssignmentId(assignment.id))
          track({
            event: 'REVIEW_CONTENT',
            details: {
              type: ActivityGroupTypes.exercise,
              title: assignment?.content?.title,
            },
          })

          navigate(`${CLIENTS_ASSIGNMENT_DETAILS.route}?assignmentId=${encodeURIComponent(assignment.id)}`, {
            state: { assignment, sessionCount: session },
          })
      }
    },
    [isAssignmentAnIntake, navigate, session, user?.id, dispatch, clientIntakeSummaryResponse?.status],
  )

  const isPreferredNameEnabledForAssignment =
    isPreferredNameEnabled &&
    clientListWithEnabledPreferredName?.length > 0 &&
    !!clientDetails?.employer &&
    (clientListWithEnabledPreferredName?.includes(clientDetails.employer) ?? false)

  const renderModalContent = () => {
    if (isEmpty(selectedAssignment)) {
      return (
        <div className={styles['loading-container']}>
          <LoadingIndicator size={45} />
        </div>
      )
    }
    if (selectedAssignment.content?.group === ActivityGroupTypes.lesson) {
      return (
        <Lesson
          onBackPress={() => onModalClose()}
          contentMetaData={selectedAssignment.content_meta_data}
          content={selectedAssignment.content}
          assignmentResponses={selectedAssignment.assignment_responses}
          assignmentId={selectedAssignment.id}
          instructions={selectedAssignment.instructions}
        />
      )
    } else if (selectedAssignment.content?.group === ActivityGroupTypes.infosheet) {
      return (
        <InfoSheet
          metaData={selectedAssignment.content_meta_data}
          instructions={selectedAssignment?.instructions ?? null}
          title={selectedAssignment.content?.title}
          onLinkClicked={(url) => Linking.openURL(url)}
          exit={() => onModalClose()}
        />
      )
    } else {
      const shouldShowAISummary =
        showAIClientIntakeSummary &&
        isAssignmentAnIntake(selectedAssignment?.content?.name) &&
        !isIntakeSessionNoteSubmitted &&
        isProviderUSCheck &&
        isLCxProviderCheck

      const clientAge = clientDetails?.date_of_birth ? getAgeISO(clientDetails.date_of_birth) : null
      const isTeenClient = clientAge !== null && isAgeTeen(clientAge)
      const isIntake = isAssignmentAnIntake(selectedAssignment?.content?.name)
      const isLCxClientIntake = clientIntakesLCx.includes(selectedAssignment?.content?.name ?? '')
      const isTeenLed = !guardianInfo.guardianLyraId
      const isDAEngageEnabled = isDAProviderMigrated && isDAMinorProgramEnabled
      const isDAEngageTeenProgram = isDAEngageProgram(programName) && isTeenProgram(programName)
      const isDaFamilyProgram = programName === ProgramNames.DirectAccessFamilyTherapy
      const isDAEngageMinorProgram = isDAMinorProgram(programName)
      const isParentLedForMinorsProgram = isDAEngageMinorProgram && !isTeenLed
      const isParentLedFamilyProgram = isDaFamilyProgram && !isTeenLed
      const caregiverName =
        (isParentLedForMinorsProgram || isParentLedFamilyProgram) &&
        guardianInfo.guardianFirstName &&
        guardianInfo.guardianLastName
          ? `${guardianInfo.guardianFirstName} ${guardianInfo.guardianLastName}`
          : undefined
      const teenLedBannerText =
        isDAEngageEnabled && (isDAEngageTeenProgram || isDaFamilyProgram) && isTeenClient && isIntake && isTeenLed ? (
          <FormattedMessage
            defaultMessage='{clientName} (teen/age {age}) booked care {reflexivePronoun}. Consider whether {possessivePronoun} parents would need to complete an intake form.'
            description='Banner text informing provider that a teen client booked their own care'
            values={{
              clientName: clientDetails?.preferred_first_name || clientDetails?.first_name || 'Client',
              age: clientAge,
              reflexivePronoun: getReflexivePronoun(clientDetails?.pronouns),
              possessivePronoun: getPossessivePronoun(clientDetails?.pronouns),
            }}
          />
        ) : undefined

      const TeenLedBanner = teenLedBannerText ? (
        <InlineWarningBanner text={teenLedBannerText} testId='TeenLedBanner' />
      ) : undefined

      const dynamicInfractionMessage = isLCxClientIntake && getClientIntakeCancellationPolicyText(selectedAssignment)

      return (
        <AssignmentReviewPage
          clientDetails={clientDetails}
          onClosePress={onModalClose}
          renderAISummarySection={
            shouldShowAISummary ? (
              <AssignmentAISummary
                selectedAssignment={selectedAssignment}
                episodeId={selectedEpisode?.id ?? ''}
                intakeAppointmentId={intakeApppointmentId}
                isAssignmentAnIntake={isAssignmentAnIntake}
              />
            ) : undefined
          }
          selectedAssignment={selectedAssignment}
          isPreferredNameEnabled={isPreferredNameEnabledForAssignment}
          alertBanner={TeenLedBanner}
          caregiverName={caregiverName}
          customCheckboxTitleComponent={dynamicInfractionMessage || undefined}
        />
      )
    }
  }

  const onBulkEditModePress = useCallback(
    (recipientType?: RecipientType) => {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: mixpanelActions.BULK_UNASSIGN_EDIT_MODE,
      })

      if (bulkEditMode && (!isDBTTeens || bulkEditRecipientType === recipientType)) {
        clearBulkEditState()
        return
      }

      const assignments =
        isDBTTeens && recipientType
          ? recipientType === RecipientType.CAREGIVER
            ? caregiverActivities
            : teenActivities
          : activitiesToDisplay
      const futureEpisodes =
        isDBTTeens && recipientType
          ? recipientType === RecipientType.CAREGIVER
            ? caregiverFutureEpisodes
            : teenFutureEpisodes
          : futureEpisodesToDisplay
      const allUnassignableItems: (Assignment | EpisodeContents)[] = []

      setBulkEditMode(true)
      setBulkEditRecipientType(isDBTTeens ? (recipientType ?? null) : null)
      setBulkActionSelectedItems([])

      if (session > currentSessionCount) {
        allUnassignableItems.push(...futureEpisodes)
      } else {
        assignments.forEach((assignment) => {
          const itemActions: ItemAction[] =
            activityActions?.filter((action: ItemAction) => action.shouldShow(assignment)) || []
          const group = assignment?.content?.group || assignment?.group
          if (
            group !== AssignmentGroup.ASSESSMENT &&
            itemActions.some((action: ItemAction) => action.name === 'unassign')
          ) {
            allUnassignableItems.push(assignment)
          }
        })
      }

      setUnassignableAssignmentsList(allUnassignableItems)
    },
    [
      activityActions,
      activitiesToDisplay,
      bulkEditMode,
      bulkEditRecipientType,
      caregiverActivities,
      caregiverFutureEpisodes,
      clearBulkEditState,
      currentSessionCount,
      futureEpisodesToDisplay,
      isDBTTeens,
      session,
      teenActivities,
      teenFutureEpisodes,
    ],
  )

  const isLoading = !contents || !clientDetails || !currentSessionCount
  const lessonIsSelected = selectedAssignment?.content?.group === ActivityGroupTypes.lesson
  const assessmentIsSelected = selectedAssignment?.content?.group === ActivityGroupTypes.assessment

  const isFullScreenOverlayOpen = (lessonIsSelected || assessmentIsSelected) && !!renderModalContent()
  const videoSessionBannerHeight = useVideoSessionBannerHeight()
  const canAddActivity =
    canManageEpisodeAssignments &&
    !config?.reducedActivitiesExperience &&
    !(
      selectedEpisode?.state !== EpisodeStates.IN_PROGRESS &&
      Array.isArray(episodes) &&
      episodes?.find((ep: Episode) => ep.state === EpisodeStates.IN_PROGRESS)
    )
  const canShowBulkEditControls =
    canManageEpisodeAssignments &&
    session >= currentSessionCount &&
    !!programConfig &&
    !!selectedEpisode &&
    !BULK_UNASSIGN_PROGRAMS_DISALLOWED.includes(selectedEpisode.program_name)

  const sharedAssignmentsListProps = useMemo(
    () => ({
      itemClickHandler: session <= currentSessionCount ? assignmentClicked : undefined,
      userRole: 'provider',
      itemActions: activityActions,
      sessionData: { sessionCount: session },
      clientFirstName,
      combineLinkedAssignments: false,
      simplifyStatus: false,
      onCheckboxPress,
      bulkActionSelectedItems,
      onSelectAllPress,
      unassignableAssignmentsList,
      assignmentsInfo: consentAssignmentsInfo,
    }),
    [
      activityActions,
      assignmentClicked,
      bulkActionSelectedItems,
      clientFirstName,
      consentAssignmentsInfo,
      currentSessionCount,
      onCheckboxPress,
      onSelectAllPress,
      session,
      unassignableAssignmentsList,
    ],
  )

  const recipientSectionSharedProps = {
    canShowBulkEditControls,
    bulkEditMode,
    bulkEditRecipientType,
    bulkActionSelectedItems,
    onConfirmPress,
    onBulkEditModePress,
    sharedAssignmentsListProps,
  }

  return (
    <div>
      <View testID={tID('ClientProgress-tab')}>
        <div>
          <div className={styles['assignment-list']}>
            {isLoading ? (
              <div className={styles['loading-container']}>
                <LoadingIndicator size={45} />
              </div>
            ) : (
              <div>
                {!isEmpty(activityNotifications) && selectedEpisode?.state === EpisodeStates.IN_PROGRESS && (
                  <NotificationBanner
                    // @ts-expect-error TS(2769): No overload matches this call.
                    notifications={activityNotifications}
                    style={{ marginBottom: '10px' }}
                    data-test-id='Assignments-NotificationBanner'
                  />
                )}
                {programName === ProgramNames.SingleSessionCoaching && selectedEpisode ? (
                  <>
                    <SSCAssignmentsList
                      activities={activities}
                      activityActions={activityActions}
                      // @ts-expect-error TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                      addTrackHandler={
                        selectedEpisode?.state !== EpisodeStates.IN_PROGRESS &&
                        Array.isArray(episodes) &&
                        episodes?.find((ep: Episode) => ep.state === EpisodeStates.IN_PROGRESS)
                          ? null
                          : addTrack
                      }
                      appointments={appointments}
                      assessments={getCurrentAssignments(assessments)}
                      currentEpisode={selectedEpisode}
                      itemClickHandler={assignmentClicked}
                      sessionCount={session}
                      userRole='provider'
                      combineLinkedAssignments={false}
                      simplifyStatus={false}
                      assignmentsInfo={consentAssignmentsInfo}
                    />
                  </>
                ) : (
                  <>
                    {!config?.reducedActivitiesExperience && (
                      <AssignmentHeaderContainer>
                        <h3 className={styles.title}>
                          {!isDBTTeens && <span>Complete before&nbsp;</span>}
                          {isDBTTeens ? (
                            <DBTTeensSessionDropdown
                              dropdownItems={getDropdownItems()}
                              testID={tID('Assignments-session-dropdown')}
                              dropdownWidth={280}
                              buttonType={ButtonType.SECONDARY}
                              selectedItemStyle={null}
                              customButtonText={`Session ${session} ( ${appointmentDateFromSessionCount(session)} )`}
                              customTextColor={themeColors.textPrimary}
                              iconColor={themeColors.textPrimary}
                              customTextSize={BodyTextSize.DEFAULT}
                            />
                          ) : (
                            <DropdownButton
                              dropdownItems={getDropdownItems()}
                              testID={tID('Assignments-session-dropdown')}
                              dropdownWidth={280}
                              buttonType={ButtonType.TERTIARY}
                              simpleHeaderStyle
                              selectedItemStyle={null}
                              useActiveTextColor
                              customButtonText={`Session ${session} ( ${appointmentDateFromSessionCount(session)} )`}
                              customTextColor={colors.primary_teal4}
                              customTextSize={BodyTextSize.LARGE}
                              style={{ backgroundColor: 'transparent' }}
                            />
                          )}
                        </h3>
                        {!isDBTTeens && !bulkEditMode && canShowBulkEditControls && (
                          <PrimaryButton
                            onPress={() => onBulkEditModePress()}
                            text='Select Activities'
                            testID={tID('Assignments-bulkEdit')}
                          />
                        )}
                        {!isDBTTeens && bulkEditMode && (
                          <AssignmentBulkActionContainer>
                            <PrimaryButton
                              disabled={bulkActionSelectedItems.length === 0}
                              onPress={onConfirmPress}
                              text='Remove'
                              testID={tID('Assignments-bulkConfirm')}
                            />
                            <AssignmentBulkActionCancelButton
                              onPress={() => onBulkEditModePress()}
                              text='Cancel'
                              testID={tID('Assignments-bulkEdit')}
                            />
                          </AssignmentBulkActionContainer>
                        )}
                      </AssignmentHeaderContainer>
                    )}
                    {isDBTTeens ? (
                      <>
                        <RecipientSection
                          {...recipientSectionSharedProps}
                          recipientType={RecipientType.TEEN}
                          name={clientFullName}
                          subtitle='Teen'
                          activities={teenActivities}
                          futureEpisodes={teenFutureEpisodes}
                          addActivityHandler={canAddActivity ? () => addActivity(RecipientType.TEEN) : undefined}
                          isFirst
                        />
                        <RecipientSection
                          {...recipientSectionSharedProps}
                          recipientType={RecipientType.CAREGIVER}
                          name={caregiverFullName || 'Caregiver'}
                          subtitle='Caregiver'
                          activities={caregiverActivities}
                          futureEpisodes={caregiverFutureEpisodes}
                          addActivityHandler={
                            canAddActivity && caregiverRecipientLyraId
                              ? () => addActivity(RecipientType.CAREGIVER)
                              : undefined
                          }
                        />
                      </>
                    ) : (
                      <AssignmentsList
                        {...sharedAssignmentsListProps}
                        addActivityHandler={canAddActivity ? () => addActivity() : undefined}
                        assignments={activitiesToDisplay}
                        futureEpisodes={futureEpisodesToDisplay}
                        bulkEditMode={bulkEditMode}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </View>
      {showModal &&
        (isFullScreenOverlayOpen ? (
          <FullScreenOverlay
            isOpen={isFullScreenOverlayOpen}
            isFocused={false}
            renderInPortal
            customStyles={
              videoSessionOpen && assessmentIsSelected ? { top: `${videoSessionBannerHeight}px`, zIndex: '10' } : {}
            }
            doNotHideAppRoot={videoSessionOpen && assessmentIsSelected}
          >
            {renderModalContent()}
          </FullScreenOverlay>
        ) : (
          <ModalContainer
            visible={showModal}
            onRequestClose={onModalClose}
            modalContents={renderModalContent()}
            videoSessionOpen={!!videoSessionOpen}
            closeOnScrim
            showCloseIcon={true}
            style={{ paddingBottom: 8 }}
          />
        ))}
      <Modal
        closeOnScrim
        visible={!!modalContent}
        onRequestClose={() => setModalContent(null)}
        modalContents={modalContent}
      />
    </div>
  )
}

export default Assignments
